import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { SteelFabTheme } from '@shared/styles/theme';

import { SteelFabLogo } from '../navbar/logo';

/**
 * @customElement site-footer-component
 */
@Component({
  selector: 'site-footer-component',
  styles: [
    css`
      ${SteelFabTheme}

      :host {
        display: block;
      }

      footer {
        background: var(--sf-bg-secondary);
        color: var(--sf-text-secondary);
        padding: 70px 0 0;
        font-family: var(--sf-font-sans);
      }

      .footer-grid {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr 1fr;
        gap: 3rem;
        padding-bottom: 3rem;
        border-bottom: 1px solid var(--sf-border-light);
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 1.25rem;
        cursor: pointer;
      }

      .brand-name {
        font-size: var(--sf-font-size-xl);
        font-weight: 700;
        color: var(--sf-text-primary);
        margin: 0;
      }

      .brand-name span {
        color: var(--sf-steel);
      }

      .brand-desc {
        font-size: var(--sf-font-size-sm);
        line-height: 1.7;
        color: var(--sf-text-secondary);
        max-width: 260px;
        margin-bottom: 1.5rem;
      }

      .social-links {
        display: flex;
        gap: 10px;
      }

      .social-btn {
        width: 38px;
        height: 38px;
        background: var(--sf-bg-secondary);
        border: 1px solid var(--sf-border-light);
        border-radius: var(--sf-radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.1rem;
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
        color: var(--sf-text-primary);
      }

      .social-btn:hover {
        background: var(--sf-steel-light);
        border-color: var(--sf-steel-light);
        color: var(--sf-text-primary);
        transform: translateY(-2px);
      }

      .col-title {
        font-size: var(--sf-font-size-sm);
        font-weight: 700;
        color: var(--sf-text-primary);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 1.25rem;
      }

      .footer-links {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
      }

      .footer-link {
        font-size: var(--sf-font-size-sm);
        color: var(--sf-text-secondary);
        cursor: pointer;
        transition: color 0.2s ease;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .footer-link:hover {
        color: var(--sf-text-primary);
      }

      .footer-link::before {
        content: '›';
        color: var(--sf-steel);
      }

      .contact-line {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 0.85rem;
        font-size: var(--sf-font-size-sm);
        color: var(--sf-text-secondary);
        a {
          color: var(--sf-text-secondary);
        }
      }

      .contact-line-icon {
        color: var(--sf-steel);
        flex-shrink: 0;
        margin-top: 1px;
      }

      .footer-bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem 0;
        font-size: var(--sf-font-size-xs);
        color: var(--sf-text-secondary);
        flex-wrap: wrap;
        gap: 1rem;
        a {
          color: var(--sf-text-primary);
        }
      }

      @media (max-width: 900px) {
        .footer-grid {
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
      }

      @media (max-width: 480px) {
        .footer-grid {
          grid-template-columns: 1fr;
        }
        .footer-bottom {
          flex-direction: column;
          text-align: center;
        }
      }
    `,
  ],
  template(this: SiteFooterComponent) {
    return html`
      <footer id="footer-component">
        <div class="container">
          <div class="footer-grid">
            <!-- Brand -->
            <div>
              <div class="brand" @click=${() => this.goto('/')}>
                ${SteelFabLogo}
                <!-- <h3 class="brand-name">Steel <span>Fab</span></h3> -->
              </div>
              <p class="brand-desc">
                Водещ доставчик на CNC машини, стругове и индустриално оборудване. Над 25 години опит и 500+ доволни
                клиенти.
              </p>
              <div class="social-links">
                <a
                  class="social-btn"
                  href="https://www.facebook.com/sweetenergy.bg"
                  title="Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  >f</a
                >
                <a class="social-btn" href="#" title="LinkedIn" target="_blank" rel="noopener noreferrer">in</a>
                <!-- <a class="social-btn" href="#" title="YouTube">▶</a> -->
              </div>
            </div>

            <!-- Категории -->
            <div>
              <div class="col-title">Продукти</div>
              <ul class="footer-links">
                <li class="footer-link" @click=${() => this.goto('/products/cnc-frezovi')}>CNC Фрезови</li>
                <li class="footer-link" @click=${() => this.goto('/products/strugove')}>Стругове</li>
                <li class="footer-link" @click=${() => this.goto('/products/shlifovachni')}>Шлифовъчни</li>
                <li class="footer-link" @click=${() => this.goto('/products/probivni')}>Пробивни</li>
                <li class="footer-link" @click=${() => this.goto('/products/zavarachno')}>Заваряване</li>
              </ul>
            </div>

            <!-- Компания -->
            <div>
              <div class="col-title">Компания</div>
              <ul class="footer-links">
                <li class="footer-link" @click=${() => this.goto('/about')}>За нас</li>
                <li class="footer-link" @click=${() => this.goto('/blog')}>Новини</li>
                <li class="footer-link" @click=${() => this.goto('/products')}>Продукти</li>
                <li class="footer-link" @click=${() => this.scrollTo()}>Контакти</li>
              </ul>
            </div>

            <!-- Контакт -->
            <div>
              <div class="col-title">Контакти</div>
              <div class="contact-line">
                <span class="contact-line-icon">📞</span>
                <a href="tel:+359885647622">+359/885-647-622</a>
              </div>
              <div class="contact-line">
                <span class="contact-line-icon">✉️</span>
                <a href="mailto:nikolay.ivanov@steelfab.bg">nikolay.ivanov@steelfab.bg</a>
              </div>
              <div class="contact-line">
                <span class="contact-line-icon">📍</span>
                <span>бл.418, ж.к. Младост 4, 1715 София</span>
              </div>
              <div class="contact-line">
                <span class="contact-line-icon">🕐</span>
                <span>Пон–Пет: 08:00 – 18:00</span>
              </div>
            </div>
          </div>

          <div class="footer-bottom">
            <span>© ${new Date().getFullYear()} Steel Fab ЕООД. Всички права запазени.</span>
            <span>Изработено с ❤️ от <a href="https://reactivum.com">Reactive Solutions</a></span>
          </div>
        </div>
      </footer>
    `;
  },
})
export class SiteFooterComponent extends LitElement {
  @Router() private router: Router;

  private goto(path: string) {
    this.router.go(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
